import { AfterViewInit, Component, ElementRef, Input, Output, ViewChild, EventEmitter, ChangeDetectionStrategy, OnInit, OnDestroy, ContentChild, TemplateRef, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { Entity } from '../../../_core/state/entities.model';

/**
 * Button Select Component
 * This component implements a radio / checkbox form UI in leiu of Material having this built in
 */
@Component({
	selector: 'app-button-select',
	templateUrl: './button-select.component.html',
	styleUrls: ['./button-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonSelectComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@ViewChild('selectButton', { read: ElementRef }) selectButtonComponent: ElementRef;
	@ViewChild(MatSelect) matSelect: MatSelect;

	@Input() options: Entity[];
	@Input() currentValue: Entity[];
	@Input() selectLayout: 'singles' | 'multiples' | 'groups' = 'singles';
	@Input() buttonText: string;
	@Input() iconName: string; // UIL Metron system
	@Input() entityValue; // Leave blank to use the whole entity as the value
	@Input() debounceTime = 50;
	@Input() applyButton = false;
	@Input() buttonStyle: 'mat-button' | 'mat-flat-button' | 'mat-stroked-button' | 'mat-mini-fab' = 'mat-flat-button';
	@Input() buttonSize: '' | 'button-large' | 'button-big' | 'button-small' = '';
	@Input() buttonColor = 'primary';
	@Output() valueChanged: EventEmitter<string> = new EventEmitter();
	@Output() applied: EventEmitter<string> = new EventEmitter<string>();
	@Output() closed: EventEmitter<string> = new EventEmitter<string>();

	overlayEl: Element;
	public itemCtrl: UntypedFormControl;

	private scrollTopBeforeSelection: number; // Hack to handle mat select jumping after selection
	private unsubscribe$: Subject<void> = new Subject();

	constructor() {}

	ngOnInit() {
		// Create a single form control to govern the selection value.
		this.itemCtrl = new UntypedFormControl(this.currentValue);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.currentValue) {
			this.currentValue = this.getOptionsEntityValues(this.currentValue);
		}
	}

	ngAfterViewInit(): void {
		const el = (this.selectButtonComponent.nativeElement as Element).querySelector('.mat-button-focus-overlay');
		if (el) {
			this.overlayEl = el;

			// Subscribe to the control changes so we can debounce it if needed
			this.itemCtrl.valueChanges
				.pipe(
					// startWith(this.currentQuery),
					takeUntil(this.unsubscribe$),
					debounceTime(this.debounceTime),
					map(options => {
						console.log('Changed', this.itemCtrl.value)
						this.valueChanged.emit(options);
					})
				)
				.subscribe();

			// HACK: We have to detect value changes and reset the scroll on the select so it doesn't jump.
			this.matSelect.openedChange
				.pipe( takeUntil(this.unsubscribe$) )
				.subscribe((open) => {
					if (open) {
						this.matSelect.panel?.nativeElement.addEventListener(
							'scroll',
							(event) => (this.scrollTopBeforeSelection = event.target.scrollTop)
						);
					}
				});

			// HACK: We have to detect value changes and reset the scroll on the select so it doesn't jump.
			this.matSelect.optionSelectionChanges
				.pipe( takeUntil(this.unsubscribe$) )
				.subscribe(() => {
					if (this.matSelect.panel) {
						this.matSelect.panel.nativeElement.scrollTop = this.scrollTopBeforeSelection;
					}
				});
		}

		console.log('Button Select', this.options, this.currentValue);
	}

	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	compareWith(a: any, b: any) {
		if (a.id) {
			return a?.id || a?.name === b?.id || b?.name;
		} else {
			return a === b;
		}
	}

	getOptionsEntityValues(options: Entity[]) {
		return options.map(option => {
			if (this.entityValue) {
				return option[this.entityValue];
			} else {
				return option;
			}
		});
	}

	applyValue() {
		this.applied.emit(this.itemCtrl.value);
		this.matSelect.close();
	}

	removeOverlay() {
		this.overlayEl?.classList.remove('mat-button-focus-overlay');
		this.closed.emit(this.itemCtrl.value);
	}

	addOverlay() {
		this.overlayEl?.classList.add('mat-button-focus-overlay');
	}

	trackByFn(index, item) {
		return item.id || item.name;
	}
}
